import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { useAddToImageLibraryMutation, ImageLibrarySource } from '../../../queries/ImageLibrary';

/**
 * Wraps an AI tab's selection callback so the chosen image joins the account's image library. Lives in
 * the AI tabs rather than the dialog shell because the generator produces an attachment for every
 * attempt, including rejected ones, and only the chosen one should be kept. Keeping the react-query
 * dependency inside the lazily-mounted tabs also keeps the dialog itself usable without a QueryClient.
 */
export function useAddAIImageToLibrary({
	addToLibrary,
	impersonationContext,
	onImageSelected,
}: {
	addToLibrary?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	onImageSelected: (image: Api.IFileAttachment) => void;
}) {
	// Without this the add fails silently and the image just never appears in the library.
	const addToImageLibraryMutation = useAddToImageLibraryMutation({
		onError: () => toast.error('Could not save that image to your library. Try again.'),
	});

	return React.useCallback(
		(image: Api.IFileAttachment) => {
			if (addToLibrary && image?.id) {
				addToImageLibraryMutation.mutate({
					fileAttachmentId: image.id,
					impersonationContext,
					source: ImageLibrarySource.AIGenerated,
				});
			}
			onImageSelected(image);
		},
		[addToLibrary, addToImageLibraryMutation, impersonationContext, onImageSelected]
	);
}
